// LO QUE ABREN LAS LIGAS DEL PIE DE LOS CORREOS · LISTO-PARA-PILOTO (b).
//
// «Darme de baja de estos avisos», «Preferencias de avisos», «¿Te fue útil
// este correo?» y «Aviso de privacidad». Antes las cuatro caían en la
// pantalla de acceso. Funciona SIN sesión cuando la liga trae su llave
// (`t`, la pega el servidor al mandar el correo); sin llave, con la sesión
// de quien entra y sobre su propia dirección.
//
// LA BAJA NO SE HACE SOLA AL ABRIR. Algunos programas de correo abren las
// ligas para revisarlas; si abrir diera de baja, un revisor automático la
// daría sin que nadie la pidiera. El botón la da, y se puede deshacer.
import { useEffect, useState } from 'react';
import { MailX, MailCheck, Loader2, ThumbsUp, ThumbsDown, FileText } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';

function Marco({ icono: Icono, titulo, subtitulo, children }) {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-md space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <Icono className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{titulo}</h1>
            <p className="text-sm text-gray-600">{subtitulo}</p>
          </div>
        </div>
        {children}
        <a href="/app" className="block text-center text-sm text-gray-600 underline">Ir a TEGO</a>
      </div>
    </div>
  );
}

function Falla({ texto }) {
  if (!texto) return null;
  return <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{texto}</p>;
}

// ---------------------------------------------------------------------
// Baja y preferencias: la misma decisión vista desde dos ligas
// ---------------------------------------------------------------------
function BajaYPreferencias({ vista, t }) {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);

  const leer = () => (t ? api.post('/correo/preferencias/leer', { t }) : api.get('/correo/preferencias/mias'));
  const accion = (que) => (t ? api.post(`/correo/${que}`, { t }) : api.post(`/correo/${que}/mia`, {}));

  useEffect(() => {
    let vivo = true;
    leer().then((d) => vivo && setEstado(d))
      .catch((e) => vivo && setError(e.message || 'No se pudo leer la preferencia. Revisa la conexión y recarga la página.'))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  async function cambiar(que) {
    setTrabajando(true);
    setError(null);
    try {
      const d = await accion(que);
      setEstado(d);
      setHecho(que);
    } catch (e) {
      setError(e.message || 'No se pudo guardar el cambio. Revisa la conexión y vuelve a pulsar.');
    } finally {
      setTrabajando(false);
    }
  }

  const titulo = vista === 'baja' ? 'Darte de baja de los avisos' : 'Preferencias de avisos por correo';
  return (
    <Marco icono={vista === 'baja' ? MailX : MailCheck} titulo={titulo}
           subtitulo="Decide si esta dirección recibe los avisos informativos de TEGO.">
      <Ayuda id="correo.preferencias.pantalla" />
      {cargando && (
        <p role="status" className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo tu preferencia…
        </p>
      )}
      <Falla texto={error} />
      {estado && (
        <div className="space-y-3">
          <p className="text-sm text-gray-800">Dirección: <strong>{estado.correo}</strong></p>
          <p role="status" className={`rounded-lg border p-2.5 text-sm ${estado.informativos
            ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
            {hecho === 'baja' && 'Listo: ya no recibirás avisos informativos. '}
            {hecho === 'alta' && 'Listo: vuelves a recibir los avisos informativos. '}
            {estado.dice}
          </p>
          {estado.informativos ? (
            <>
              <button type="button" onClick={() => cambiar('baja')} disabled={trabajando}
                      className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-3 text-base font-semibold text-white disabled:opacity-60">
                {trabajando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                <MailX className="h-5 w-5" aria-hidden="true" /> Darme de baja de estos avisos
              </button>
              <Ayuda id="correo.baja.boton" />
            </>
          ) : (
            <button type="button" onClick={() => cambiar('alta')} disabled={trabajando}
                    className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-semibold text-gray-900 disabled:opacity-60">
              {trabajando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
              <MailCheck className="h-5 w-5" aria-hidden="true" /> Volver a recibir los avisos
            </button>
          )}
        </div>
      )}
    </Marco>
  );
}

// ---------------------------------------------------------------------
// «¿Te fue útil este correo?»
// ---------------------------------------------------------------------
function Opinion({ v, m }) {
  const [voto, setVoto] = useState(v === 'si' || v === 'no' ? v : null);
  const [respuesta, setRespuesta] = useState(null);
  const [error, setError] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  async function votar(cual) {
    setTrabajando(true);
    setError(null);
    try {
      setRespuesta(await api.post('/correo/opinion', { v: cual, m }));
      setVoto(cual);
    } catch (e) {
      setError(e.message || 'No se pudo anotar tu opinión. Revisa la conexión y vuelve a pulsar.');
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Marco icono={voto === 'no' ? ThumbsDown : ThumbsUp} titulo="¿Te fue útil este correo?"
           subtitulo="Tu voto ayuda a revisar qué correos sirven y cuáles sobran.">
      <Ayuda id="correo.opinion.pantalla" />
      {respuesta ? (
        <p role="status" className="rounded-lg border border-green-200 bg-green-50 p-2.5 text-sm text-green-800">{respuesta.dice}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => votar('si')} disabled={trabajando}
                  className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border px-4 py-3 text-base font-semibold disabled:opacity-60 ${voto === 'si' ? 'border-[var(--color-marca)] bg-[var(--color-marca)] text-white' : 'border-gray-300 bg-white text-gray-900'}`}>
            <ThumbsUp className="h-5 w-5" aria-hidden="true" /> Sí me sirvió
          </button>
          <button type="button" onClick={() => votar('no')} disabled={trabajando}
                  className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border px-4 py-3 text-base font-semibold disabled:opacity-60 ${voto === 'no' ? 'border-[var(--color-marca)] bg-[var(--color-marca)] text-white' : 'border-gray-300 bg-white text-gray-900'}`}>
            <ThumbsDown className="h-5 w-5" aria-hidden="true" /> No me sirvió
          </button>
        </div>
      )}
      <Falla texto={error} />
    </Marco>
  );
}

// ---------------------------------------------------------------------
// El aviso de privacidad, sin sesión (el mismo texto que la puerta)
// ---------------------------------------------------------------------
function Legal({ clave }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get(`/legal/${clave}`).then((d) => vivo && setDoc(d))
      .catch((e) => vivo && setError(e.message || 'No se pudo abrir el documento. Revisa la conexión y recarga la página.'));
    return () => { vivo = false; };
  }, [clave]);
  return (
    <Marco icono={FileText} titulo={doc?.titulo || 'Aviso de privacidad'}
           subtitulo="Qué datos guarda TEGO, para qué, y cómo pedir que se corrijan o se borren.">
      {!doc && !error && (
        <p role="status" className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Abriendo el documento…
        </p>
      )}
      <Falla texto={error} />
      {doc?.aviso && <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900">{doc.aviso}</p>}
      {doc && <div className="max-h-[60vh] overflow-auto whitespace-pre-wrap text-sm text-gray-800">{doc.texto}</div>}
    </Marco>
  );
}

export default function PantallaDelCorreo({ destino }) {
  if (destino.vista === 'opinion') return <Opinion v={destino.v} m={destino.m} />;
  if (destino.vista === 'legal') return <Legal clave={destino.clave} />;
  return <BajaYPreferencias vista={destino.vista} t={destino.t} />;
}
